import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`ri-book-read-line text-2xl ${isScrolled || !isHome ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span className={`text-lg font-bold font-heading whitespace-nowrap ${isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'}`}>
              韩国漫画网
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isHome
                  ? isScrolled
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-background-50 bg-white/20'
                  : location.pathname === '/'
                  ? 'text-primary-500 bg-primary-50'
                  : isScrolled || !isHome
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  isCategoryOpen
                    ? isScrolled || !isHome
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-background-50 bg-white/20'
                    : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                分类
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isCategoryOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsCategoryOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2 z-50">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors cursor-pointer"
                        onClick={() => setIsCategoryOpen(false)}
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-50'
                  : isScrolled || !isHome
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                <i className={`ri-search-line text-sm ${isScrolled || !isHome ? 'text-foreground-400' : 'text-background-50/60'}`}></i>
              </div>
              <input
                type="text"
                placeholder="搜索漫画..."
                className={`pl-9 pr-4 py-2 rounded-full text-sm w-48 focus:w-64 transition-all duration-300 outline-none ${
                  isScrolled || !isHome
                    ? 'bg-background-100 text-foreground-950 placeholder:text-foreground-400 border border-background-200/70 focus:border-primary-400'
                    : 'bg-white/15 text-background-50 placeholder:text-background-50/50 border border-white/20 focus:bg-white/25'
                }`}
              />
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70 mt-0">
            <div className="flex flex-col gap-1 pt-3">
              <Link to="/" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 cursor-pointer">首页</Link>
              <div className="font-medium text-xs text-foreground-400 px-4 pt-2 pb-1">漫画分类</div>
              {categories.slice(0, 6).map((cat) => (
                <Link key={cat.slug} to={`/category/${cat.slug}`} className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cat.icon} text-base`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
              <Link to="/about" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 cursor-pointer">关于我们</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}